import { Component, Input } from '@angular/core';
import {
  ArtifactVM,
  SoftwareSelectionResolutionDto,
} from 'src/app/models/device.model';
import {
  ReleaseBundleArtifactDto,
  ReleaseBundleDto,
  ReleaseDto,
} from 'src/app/models/license.model';
import {
  ReleaseOverrideOption,
  SoftwareOverrideComponent,
} from '../device-software-override.types';

export interface SelectedBundleOverride {
  release?: ReleaseDto;
  bundle: ReleaseBundleDto;
}

@Component({
  selector: 'app-device-software-override-review',
  templateUrl: './device-software-override-review.component.html',
  styleUrl: './device-software-override-review.component.scss',
  standalone: false,
})
export class DeviceSoftwareOverrideReviewComponent {
  @Input() components: SoftwareOverrideComponent[] = [];
  @Input() bundleOverrides: ReleaseBundleDto[] = [];
  @Input() releaseOptions: ReleaseOverrideOption[] = [];
  @Input() supportedProjectIds: string[] = [];
  @Input() softwareSelectionResolutions: SoftwareSelectionResolutionDto[] = [];
  @Input() forLicenseBatch = false;
  @Input() unresolvedOverrideIds: string[] = [];

  isUnresolved(id: string): boolean {
    return this.unresolvedOverrideIds.includes(id);
  }

  get selectedArtifacts(): ArtifactVM[] {
    return this.components
      .map((component) => component.selectedArtifact)
      .filter((artifact): artifact is ArtifactVM => !!artifact);
  }

  get selectedBundles(): SelectedBundleOverride[] {
    return this.bundleOverrides.map((bundle) => ({
      release: this.releaseOptions.find(
        (option) =>
          option.selectedBundle?.id === bundle.id ||
          option.release.id === this.getBundleReleaseId(bundle),
      )?.release,
      bundle,
    }));
  }

  getRelevantBundleArtifacts(
    bundle: ReleaseBundleDto,
  ): ReleaseBundleArtifactDto[] {
    const supportedProjectIds = new Set(this.supportedProjectIds);
    const artifacts =
      supportedProjectIds.size === 0
        ? (bundle.artifacts ?? [])
        : (bundle.artifacts ?? []).filter((artifact) =>
            supportedProjectIds.has(artifact.projectId),
          );

    return this.sortBundleArtifacts(artifacts);
  }

  getOtherBundleArtifacts(
    bundle: ReleaseBundleDto,
  ): ReleaseBundleArtifactDto[] {
    const supportedProjectIds = new Set(this.supportedProjectIds);
    if (supportedProjectIds.size === 0) {
      return [];
    }

    return this.sortBundleArtifacts(
      (bundle.artifacts ?? []).filter(
        (artifact) => !supportedProjectIds.has(artifact.projectId),
      ),
    );
  }

  private sortBundleArtifacts(
    artifacts: ReleaseBundleArtifactDto[],
  ): ReleaseBundleArtifactDto[] {
    return [...artifacts].sort((first, second) =>
      (first.projectName || first.projectId).localeCompare(
        second.projectName || second.projectId,
      ),
    );
  }

  private getBundleReleaseId(bundle: ReleaseBundleDto): string | undefined {
    return bundle.releaseId;
  }
}
